import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Button, Card, CardContent, Typography, Stack, Divider, Chip, Tooltip, CircularProgress
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { API_BASE, fetchJson } from "../api";
import TextField from '@mui/material/TextField';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

const Shell = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: theme.palette.mode === "dark" ? "#0E1116" : "#F6F7FB",
}));
const Panel = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
}));

function normalizeNote(n) {
  if (!n) return null;
  const tcSmpte = n.tc_smpte ?? n.tcSmpte ?? "00:00:00:00";
  const text = typeof n.text === "string" ? n.text : "";
  const category = n.category ?? "Contenuti";
  const resolved = Boolean(n.resolved);
  // ordino usando tc_seconds se presente, altrimenti parsando SMPTE (grezzo)
  const tcSeconds = typeof n.tc_seconds === "number" ? n.tc_seconds : null;
  return {
    id: n.id ?? n.note_id ?? null,
    tc_smpte: tcSmpte,
    tc_seconds: tcSeconds,
    text,
    category,
    resolved,
  };
}

export default function ReviewDashboard() {
  const { id: reviewId } = useParams();

  const [notes, setNotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [todos, setTodos] = useState(null);
  const [linkMeta, setLinkMeta] = useState(null);

  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingTodos, setLoadingTodos] = useState(false);
  const [err, setErr] = useState(null);

  const loadNotes = async () => {
    const data = await fetchJson(`/reviews/${reviewId}/notes`);
    const arr = Array.isArray(data) ? data : Array.isArray(data?.notes) ? data.notes : [];
    const norm = arr.map(normalizeNote).filter(Boolean);
    // ordina per tc_seconds se c'è, altrimenti lascia così
    norm.sort((a, b) => {
      if (a.tc_seconds == null && b.tc_seconds == null) return 0;
      if (a.tc_seconds == null) return 1;
      if (b.tc_seconds == null) return -1;
      return a.tc_seconds - b.tc_seconds;
    });
    setNotes(norm);
      try { const meta = await fetchJson(`/reviews/${reviewId}`); setLinkMeta(meta); } catch (e) { console.warn('No meta', e); }
  };

  useEffect(() => {
    if (!reviewId) return;
    loadNotes().catch(() => setErr("Impossibile caricare le note"));
  }, [reviewId]);

  const payloadNotes = useMemo(
    () => ({ notes: notes.map(n => ({ tc_smpte: n.tc_smpte, text: n.text })) }),
    [notes]
  );


  const handleSummary = async () => {
    setLoadingSummary(true); setErr(null);
    try {
      const data = await fetchJson(`/ai/reviews/${reviewId}/summary`, {
        method: "POST",
        body: JSON.stringify(payloadNotes),
      });
      setSummary(data || null);
    } catch (e) {
      // prova a mostrare l’errore del server se presente
      setErr("Riassunto AI non riuscito");
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleTodos = async () => {
    setLoadingTodos(true); setErr(null);
    try {
      const data = await fetchJson(`/ai/reviews/${reviewId}/todos`, {
        method: "POST",
        body: JSON.stringify(payloadNotes),
      });
      setTodos(data || null);
    } catch (e) {
      setErr("To-Do AI non riuscito");
    } finally {
      setLoadingTodos(false);
    }
  };

  const exportPdf = () => { window.open(`${API_BASE}/reviews/${reviewId}/export/pdf`, '_blank'); };

  const exportTodosCSV = () => {
  const version = (linkMeta?.version_label || linkMeta?.target_role || "").trim();
  const rows = [
    ["Versione","TC","Task","Reparto","Priorità"],
    ...((todos?.tasks) || []).map(t => [version, t.tc ?? "", t.text ?? "", t.dept ?? "", t.priority ?? ""])
  ];
  const csv = rows.map(r => r.map(x => `"${String(x).replace(/"/g,'""')}"`).join(";")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `todo_${reviewId}.csv`; a.click();
  URL.revokeObjectURL(url);
};

  const exportNotesCSV = () => {
  const version = (linkMeta?.version_label || linkMeta?.target_role || "").trim();
  const rows = [
    ["TC","Versione","Nota","Autore","Data","FPS"],
    ...notes.map(n => [
      n.tc_smpte || "",
      version,
      (n.text || "").replace(/\\n/g," "),
      n.author_label || "",
      n.created_at ? new Date(n.created_at).toLocaleString("it-IT") : "",
      n.fps ?? 25
    ])
  ];
  const csv = rows.map(r => r.map(x => `"${String(x).replace(/"/g,'""')}"`).join(";")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `note_${reviewId}.csv`; a.click();
  URL.revokeObjectURL(url);
};

  if (!reviewId) return <Box sx={{ p: 3 }}>Link interno non valido</Box>;

  return (
    <Shell sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        {/* Header */}
        <Panel sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              Dashboard Review – ID {reviewId}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Sintesi e task operativi dalle note cliente.
            </Typography>
          </CardContent>
        </Panel>

        {/* Azioni AI */}
        <Panel sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                onClick={handleSummary}
                disabled={loadingSummary || notes.length === 0}
                startIcon={loadingSummary ? <CircularProgress size={16} /> : null}
              >
                Genera Riassunto AI
              </Button>
              <Button
                variant="outlined"
                onClick={handleTodos}
                disabled={loadingTodos || notes.length === 0}
                startIcon={loadingTodos ? <CircularProgress size={16} /> : null}
              >
                Genera To-Do AI
              </Button>
              <Chip label={`Note: ${notes.length}`} />
              <Tooltip title="Esporta elenco note in CSV">
                <Button size="small" startIcon={<DownloadRoundedIcon />} onClick={exportNotesCSV}>
                  Export Note
                </Button>
              </Tooltip>
              {todos?.tasks?.length > 0 && (
                <Tooltip title="Esporta To-Do in CSV">
                  <Button size="small" startIcon={<DownloadRoundedIcon />} onClick={exportTodosCSV}>
                    Export To-Do
                  </Button>
                </Tooltip>
              )}
            </Stack>
            {err && <Typography sx={{ mt: 1 }} color="error">{err}</Typography>}
          </CardContent>
        </Panel>

        {/* Elenco note */}
        <Panel sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Note cliente</Typography>
            {notes.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Nessuna nota per questa review.
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #eee" }}>TC</th>
                      <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #eee" }}>Nota</th><th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #eee" }}>Autore</th><th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #eee" }}>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notes.map((n, i) => (<tr key={n.id ?? `${n.tc_smpte}-${i}`}><td style={{ padding: 8, whiteSpace: 'nowrap' }}>{n.tc_smpte}</td><td style={{ padding: 8 }}>{linkMeta?.version_label || linkMeta?.target_role || '—'}</td><td style={{ padding: 8 }}>{n.text}</td><td style={{ padding: 8, minWidth: 220 }}><TextField size='small' fullWidth value={n.resolution_comment || ''} onChange={(e)=>{ const v=e.target.value; setNotes(prev=>prev.map(x=>x.id===n.id?{...x,resolution_comment:v}:x)); }} onBlur={async (e)=>{ try{ await fetchJson(`/reviews/${reviewId}/notes/${n.id}`, { method:'PUT', body: JSON.stringify({ resolution_comment: e.target.value }) }); } catch(e){} }} placeholder='Scrivi cosa è stato fatto…' /></td><td style={{ padding: 8 }}>{n.author_label || ''}</td><td style={{ padding: 8 }}>{n.created_at ? new Date(n.created_at).toLocaleString('it-IT') : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </CardContent>
        </Panel>

        {/* Output AI */}
        {summary && (
          <Panel sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Riassunto</Typography>
              {summary.summary && <Typography sx={{ mb: 1.5 }}>{summary.summary}</Typography>}
              {Array.isArray(summary.highlights) && summary.highlights.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Punti chiave</Typography>
                  <ul style={{ marginTop: 0 }}>
                    {summary.highlights.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </>
              )}
              {Array.isArray(summary.risks) && summary.risks.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Rischi</Typography>
                  <ul style={{ marginTop: 0 }}>
                    {summary.risks.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </>
              )}
            </CardContent>
          </Panel>
        )}

        {todos && (
          <Panel>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>To-Do operativi</Typography>
              {(todos.tasks || []).length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>Nessun task generato.</Typography>
              ) : (
                <ul style={{ marginTop: 0 }}>
                  {todos.tasks.map((t, i) => (
                    <li key={i}>[{t.tc ?? "—"}] {t.text} — <em>{t.dept}</em> ({t.priority})</li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Panel>
        )}
      </Box>
    </Shell>
  );
}
